import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { getUtilizationColor } from '../../utils/calculations';

interface ProgressBarProps {
  value: number;
  max: number;
  height?: 'sm' | 'md' | 'lg';
  showPercentage?: boolean;
  colorByUtilization?: boolean;
  customColor?: string;
  showMilestones?: boolean;
}

const MILESTONES = [25, 50, 75, 100];

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max,
  height = 'md',
  showPercentage = false,
  colorByUtilization = true,
  customColor,
  showMilestones = false,
}) => {
  const { darkMode } = useTheme();

  const percentage = max > 0 ? (value / max) * 100 : 0;
  const clampedPercentage = Math.min(percentage, 100);

  const heightClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-3',
  };

  const barColor = customColor || (colorByUtilization ? getUtilizationColor(percentage) : 'bg-violet-500');

  return (
    <div className="w-full">
      <div className="relative">
        <div
          className={`
            ${heightClasses[height]} rounded-full overflow-hidden
            ${darkMode ? 'bg-white/10' : 'bg-slate-200'}
          `}
        >
          <div
            className={`h-full rounded-full transition-all ${barColor}`}
            style={{ width: `${clampedPercentage}%` }}
          />
        </div>
        {showMilestones && MILESTONES.map(ms => (
          <div
            key={ms}
            className={`absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full border ${
              percentage >= ms
                ? darkMode ? 'bg-violet-400 border-violet-400' : 'bg-violet-500 border-violet-500'
                : darkMode ? 'bg-slate-600 border-slate-500' : 'bg-slate-300 border-slate-400'
            }`}
            style={{ left: `${ms}%`, marginLeft: '-3px' }}
          />
        ))}
      </div>
      {showPercentage && (
        <span className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          {percentage.toFixed(0)}%
        </span>
      )}
    </div>
  );
};

interface CreditUtilizationBarProps {
  name: string;
  balance: number;
  limit: number;
  onBalanceClick?: () => void;
  onLimitClick?: () => void;
  privacyMode?: boolean;
  balanceElement?: React.ReactNode;
  limitElement?: React.ReactNode;
}

export const CreditUtilizationBar: React.FC<CreditUtilizationBarProps> = ({
  name,
  balance,
  limit,
  privacyMode = false,
  balanceElement,
  limitElement,
}) => {
  const { darkMode } = useTheme();
  const utilization = limit > 0 ? (balance / limit) * 100 : 0;

  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          {name}
        </span>
        <span className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
          {privacyMode ? '••••' : `${utilization.toFixed(0)}%`}
        </span>
      </div>
      <ProgressBar value={balance} max={limit} height="sm" />
      <div className="flex justify-between mt-1">
        {balanceElement}
        {limitElement}
      </div>
    </div>
  );
};
